import { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, XCircle, TrendingDown, ExternalLink } from 'lucide-react';
import { Button } from './ui/Button';
import { Branding } from './Branding';

interface ResultsSectionProps {
  hotelName: string;
  hotelLocation: string;
  hotelStars: number;
  selectedOTAs: string[];
}

interface OTAResult {
  id: string;
  name: string;
  otaPrice: number;
  standardPrice: number;
  difference: number;
  differencePercent: number;
  status: 'optimal' | 'moderate' | 'critical';
  roomType: string;
  roomImage: string;
}

const OTA_NAMES: Record<string, string> = {
  booking: 'Booking.com',
  expedia: 'Expedia',
  hotels: 'Hotels.com',
  agoda: 'Agoda',
};

const ROOM_IMAGES = [
  'https://images.unsplash.com/photo-1611892440504-42a792e24d32?q=80&w=800',
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?q=80&w=800',
  'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?q=80&w=800',
  'https://images.unsplash.com/photo-1595576508898-0ad5c879a061?q=80&w=800',
];

const ROOM_TYPES = [
  'Deluxe King Room',
  'Superior Double Room',
  'Executive Suite',
  'Classic Twin Room',
];

const generateMockResults = (otas: string[]): OTAResult[] => {
  return otas.map((ota, index) => {
    const standardPrice = 189;
    const variance = Math.random();
    let otaPrice: number;
    let status: 'optimal' | 'moderate' | 'critical';

    if (variance < 0.3) {
      otaPrice = standardPrice + Math.floor(Math.random() * 20);
      status = 'optimal';
    } else if (variance < 0.7) {
      otaPrice = standardPrice - Math.floor(Math.random() * 20 + 5);
      status = 'moderate';
    } else {
      otaPrice = standardPrice - Math.floor(Math.random() * 40 + 25);
      status = 'critical';
    }

    const difference = standardPrice - otaPrice;
    const differencePercent = Math.round((difference / standardPrice) * 100);

    return {
      id: ota,
      name: OTA_NAMES[ota] || ota,
      otaPrice,
      standardPrice,
      difference,
      differencePercent,
      status,
      roomType: ROOM_TYPES[index % ROOM_TYPES.length],
      roomImage: ROOM_IMAGES[index % ROOM_IMAGES.length],
    };
  });
};

const calculateScore = (results: OTAResult[]): number => {
  const totalOTAs = results.length;
  let score = 100;

  results.forEach((result) => {
    if (result.status === 'moderate') {
      score -= (15 / totalOTAs) * totalOTAs;
    } else if (result.status === 'critical') {
      score -= (35 / totalOTAs) * totalOTAs;
    }
  });

  return Math.max(0, Math.round(score));
};

export const ResultsSection = ({
  hotelName,
  hotelLocation,
  hotelStars,
  selectedOTAs,
}: ResultsSectionProps) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [results] = useState(() => generateMockResults(selectedOTAs));
  const score = calculateScore(results);

  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const increment = score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setAnimatedScore(score);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.round(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [score]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'ha-text-success';
    if (score >= 40) return 'ha-text-warning';
    return 'ha-text-destructive';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 40) return 'Needs Attention';
    return 'Critical';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'optimal':
        return <CheckCircle className="ha-w-5 ha-h-5 ha-text-success" />;
      case 'moderate':
        return <AlertTriangle className="ha-w-5 ha-h-5 ha-text-warning" />;
      case 'critical':
        return <XCircle className="ha-w-5 ha-h-5 ha-text-destructive" />;
    }
  };

  const getStatusBadge = (status: string, percent: number) => {
    switch (status) {
      case 'optimal':
        return (
          <span className="ha-px-3 ha-py-1 ha-rounded-full ha-text-xs ha-font-medium ha-bg-success\\/10 ha-text-success ha-border ha-border-success\\/20">
            ✓ Optimal
          </span>
        );
      case 'moderate':
        return (
          <span className="ha-px-3 ha-py-1 ha-rounded-full ha-text-xs ha-font-medium ha-bg-warning\\/10 ha-text-warning ha-border ha-border-warning\\/20">
            ⚠ -{percent}% Undercut
          </span>
        );
      case 'critical':
        return (
          <span className="ha-px-3 ha-py-1 ha-rounded-full ha-text-xs ha-font-medium ha-bg-destructive\\/10 ha-text-destructive ha-border ha-border-destructive\\/20">
            ✗ -{percent}% Critical
          </span>
        );
    }
  };

  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  return (
    <section id="ha-results" className="ha-py-16 ha-animate-slide-up">
      <div className="ha-container ha-mx-auto ha-px-4">
        <div className="ha-max-w-5xl ha-mx-auto">
          {/* Enhanced Score Card */}
          <div className="ha-glass-card ha-p-8 ha-md:p-12 ha-mb-8">
            <div className="ha-flex ha-flex-col ha-lg:flex-row ha-items-center ha-gap-10">
              {/* Score Gauge */}
              <div className="ha-relative">
                <div className="ha-absolute ha-inset-0 ha-animate-pulse ha-opacity-20">
                  <div className={`ha-w-full ha-h-full ha-rounded-full ${getScoreColor(score)} ha-glow-primary`}></div>
                </div>

                <svg className="ha-w-44 ha-h-44 ha--rotate-90" viewBox="0 0 160 160">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="10"
                    className="ha-text-muted"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="10"
                    strokeLinecap="round"
                    className={getScoreColor(score)}
                    style={{
                      strokeDasharray: circumference,
                      strokeDashoffset,
                      transition: 'stroke-dashoffset 1.5s ease-out',
                      filter: 'drop-shadow(0 0 8px currentColor)',
                    }}
                  />
                </svg>

                <div className="ha-absolute ha-inset-0 ha-flex ha-flex-col ha-items-center ha-justify-center">
                  <span className={`ha-text-4xl ha-font-bold ${getScoreColor(score)}`}>
                    {animatedScore}
                  </span>
                  <span className="ha-text-sm ha-text-muted-foreground">/100</span>
                </div>
              </div>

              {/* Score Summary */}
              <div className="ha-flex-1 ha-text-center ha-lg:text-left">
                <h3 className="ha-text-2xl ha-font-bold ha-mb-2">
                  Undercut Score for <span className="ha-text-gradient">{hotelName}</span>
                </h3>
                <p className={`ha-text-lg ha-font-medium ${getScoreColor(score)} ha-mb-4`}>
                  {getScoreLabel(score)}
                </p>
                <p className="ha-text-muted-foreground ha-mb-6">
                  {score >= 80
                    ? 'Your pricing is well-aligned across all monitored OTAs. Keep up the great work!'
                    : score >= 40
                    ? 'Some OTAs are selling your rooms below your standard rate. Review the details below.'
                    : 'Critical undercuts detected! OTAs are significantly undercutting your rates.'}
                </p>
                <div className="ha-flex ha-flex-wrap ha-gap-3 ha-justify-center ha-lg:justify-start">
                  <Button>Get Detailed Report</Button>
                  <Button variant="outline">Set Up Alerts</Button>
                </div>
              </div>
            </div>
          </div>

          {/* OTA Comparison Cards with Room & Hotel Info */}
          <h4 className="ha-text-lg ha-font-semibold ha-mb-4 ha-flex ha-items-center ha-gap-2">
            <TrendingDown className="ha-w-5 ha-h-5 ha-text-primary" />
            Price Comparison by Platform
          </h4>

          <div className="ha-grid ha-grid-cols-1 ha-md:grid-cols-2 ha-gap-4 ha-mb-8">
            {results.map((result, index) => (
              <div
                key={result.id}
                className="ha-glass-card ha-p-6 ha-animate-fade-in ha-transition-all ha-duration-300 hover:ha-scale-105"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Header with OTA name and status */}
                <div className="ha-flex ha-items-start ha-justify-between ha-mb-4">
                  <div className="ha-flex ha-items-center ha-gap-3">
                    {getStatusIcon(result.status)}
                    <h5 className="ha-font-semibold">{result.name}</h5>
                  </div>
                  {getStatusBadge(result.status, Math.abs(result.differencePercent))}
                </div>

                {/* Room Image and Type */}
                <div className="ha-mb-4">
                  <img
                    src={result.roomImage}
                    alt={result.roomType}
                    className="ha-w-full ha-h-32 ha-object-cover ha-rounded-lg ha-mb-2"
                  />
                  <div className="ha-text-sm ha-font-medium ha-text-primary">
                    🛏️ {result.roomType}
                  </div>
                </div>

                {/* Hotel Information */}
                <div className="ha-mb-4 ha-p-3 ha-bg-secondary\\/30 ha-rounded-lg ha-border ha-border-border\\/50">
                  <div className="ha-text-xs ha-text-muted-foreground ha-mb-1">Hôtel:</div>
                  <div className="ha-text-sm ha-font-semibold ha-mb-1">{hotelName}</div>
                  <div className="ha-text-xs ha-text-muted-foreground">
                    📍 {hotelLocation} • {'⭐'.repeat(hotelStars)} {hotelStars} étoiles
                  </div>
                </div>

                {/* Price Comparison */}
                <div className="ha-grid ha-grid-cols-2 ha-gap-4 ha-mb-4">
                  <div>
                    <p className="ha-text-xs ha-text-muted-foreground ha-mb-1">OTA Price</p>
                    <p className="ha-text-2xl ha-font-bold ha-text-primary">€{result.otaPrice}</p>
                  </div>
                  <div>
                    <p className="ha-text-xs ha-text-muted-foreground ha-mb-1">Your Standard</p>
                    <p className="ha-text-2xl ha-font-bold ha-text-muted-foreground">
                      €{result.standardPrice}
                    </p>
                  </div>
                </div>

                {/* Undercut Alert */}
                {result.status !== 'optimal' && (
                  <div
                    className={`ha-p-3 ha-rounded-lg ha-border ha-mb-3 ${
                      result.status === 'moderate'
                        ? 'ha-bg-warning\\/5 ha-border-warning\\/20'
                        : 'ha-bg-destructive\\/5 ha-border-destructive\\/20'
                    }`}
                  >
                    <p className="ha-text-sm">
                      <span
                        className={
                          result.status === 'moderate'
                            ? 'ha-text-warning ha-font-semibold'
                            : 'ha-text-destructive ha-font-semibold'
                        }
                      >
                        €{Math.abs(result.difference)} less
                      </span>
                      <span className="ha-text-muted-foreground"> than your direct rate</span>
                    </p>
                  </div>
                )}

                {/* View on OTA Link */}
                <button
                  className="ha-flex ha-items-center ha-gap-2 ha-text-sm ha-text-primary hover:ha-underline ha-bg-transparent ha-border-none ha-cursor-pointer ha-transition-all"
                  style={{ padding: 0 }}
                >
                  View on {result.name}
                  <ExternalLink className="ha-w-3 ha-h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="ha-glass-card ha-p-8 ha-text-center">
            <h4 className="ha-text-xl ha-font-bold ha-mb-3">Want continuous monitoring?</h4>
            <p className="ha-text-muted-foreground ha-mb-6">
              Upgrade to Pro for real-time alerts, detailed analytics, and priority support.
            </p>
            <Button size="lg" className="ha-glow-primary">
              Upgrade to Pro
            </Button>

            <Branding />
          </div>
        </div>
      </div>
    </section>
  );
};